import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { useForm } from "react-hook-form";
import { H3 } from "../../../../../components/Typography/Typography";
import { SettingsCard } from "../../../../../layout/portalPageLayout";
import { FilePickerUncontrolled } from "../../../../../components/FilePicker/FilePickerUncontrolled";
import { PrimaryButtonMedium } from "../../../../../components/Buttons/Buttons";
import { useStoreState } from "../../../../../util/util";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import axios from "axios";

const FormContainer = styled.form`
  margin-top: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const SubmitButton = styled(PrimaryButtonMedium)`
  margin-top: ${({ theme }) => theme.resolve("spacing_3xl")};
  min-width: ${({ theme }) => theme.resolve("width_xxxs")};
`;

const MessageContainer = styled.div<{ type: "success" | "error" }>`
  margin-top: ${({ theme }) => theme.resolve("spacing_2xl")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  background-color: ${({ type, theme }) =>
    type === "success"
      ? theme.resolve("color_bg_surface_success")
      : theme.resolve("color_bg_surface_error")};
  color: ${({ type, theme }) =>
    type === "success"
      ? theme.resolve("color_text_success")
      : theme.resolve("color_text_error")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ type, theme }) =>
      type === "success"
        ? theme.resolve("color_border_success")
        : theme.resolve("color_border_error")};
`;

const MessageTitle = styled.div`
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
`;

const MessageDetail = styled.div`
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
`;

interface FormData {
  file: File;
}

interface UploadSuccessResponse {
  storefront_id: string;
  filename: string;
  total_rows: number;
  records_created: number;
  resolved: {
    leads: number;
    sample_requests: number;
    quote_requests: number;
  };
}

interface UploadErrorResponse {
  error_id: string;
  status_code: string;
  message: string;
}

export const AlchemistAITab = () => {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadResponse, setUploadResponse] =
    useState<{
      type: "success" | "error";
      data: UploadSuccessResponse | UploadErrorResponse | null;
    } | null>(null);

  const methods = useForm<FormData>({
    mode: "onChange",
  });

  const { handleSubmit, reset } = methods;

  const onSubmit = async (data: FormData) => {
    if (!data.file) {
      notifyError(t("Please select a file to upload"));
      return;
    }

    setIsSubmitting(true);
    setUploadResponse(null);

    try {
      const formData = new FormData();
      formData.append("file", data.file);

      const response = await axios.post<UploadSuccessResponse>(
        `/v2/storefronts/${storefront_id}/lead-intel/uploads`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUploadResponse({
        type: "success",
        data: response.data,
      });
      notifySuccess(t("File uploaded successfully"));
      reset();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const errorData = error.response.data as UploadErrorResponse;
        setUploadResponse({
          type: "error",
          data: errorData,
        });
        notifyError(errorData.message || t("Failed to upload file"));
      } else {
        setUploadResponse({
          type: "error",
          data: null,
        });
        notifyError(t("Failed to upload file"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SettingsCard>
      <H3>{t("Lead Intel Upload")}</H3>
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <FilePickerUncontrolled
          name="file"
          methodsOfUseForm={methods}
          required={true}
          accept={[".csv"]}
          placeHolderText={t("Drag and drop a CSV file here or")}
          description={t("Upload lead intelligence data in CSV format")}
        />
        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? t("Uploading...") : t("Upload")}
        </SubmitButton>
      </FormContainer>

      {uploadResponse &&
        uploadResponse.type === "success" &&
        uploadResponse.data && (
          <MessageContainer type="success">
            <MessageTitle>{t("Upload Successful")}</MessageTitle>
            <MessageDetail>
              {t("Filename")}:{" "}
              {(uploadResponse.data as UploadSuccessResponse).filename}
              <br />
              {t("Total rows")}:{" "}
              {(uploadResponse.data as UploadSuccessResponse).total_rows}
              <br />
              {t("Records created")}:{" "}
              {(uploadResponse.data as UploadSuccessResponse).records_created}
              <br />
              {t("Resolved leads")}:{" "}
              {(uploadResponse.data as UploadSuccessResponse).resolved.leads}
              <br />
              {t("Resolved sample requests")}:{" "}
              {
                (uploadResponse.data as UploadSuccessResponse).resolved
                  .sample_requests
              }
              <br />
              {t("Resolved quote requests")}:{" "}
              {
                (uploadResponse.data as UploadSuccessResponse).resolved
                  .quote_requests
              }
            </MessageDetail>
          </MessageContainer>
        )}

      {uploadResponse && uploadResponse.type === "error" && (
        <MessageContainer type="error">
          <MessageTitle>{t("Upload Failed")}</MessageTitle>
          <MessageDetail>
            {uploadResponse.data &&
            (uploadResponse.data as UploadErrorResponse).message
              ? (uploadResponse.data as UploadErrorResponse).message
              : t("An unexpected error occurred")}
            {uploadResponse.data &&
              (uploadResponse.data as UploadErrorResponse).error_id && (
                <>
                  <br />
                  {t("Error ID")}:{" "}
                  {(uploadResponse.data as UploadErrorResponse).error_id}
                </>
              )}
          </MessageDetail>
        </MessageContainer>
      )}
    </SettingsCard>
  );
};
